import React from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import NotificationBell from "@/components/NotificationBell";
import ProfileMenu from "@/components/ProfileMenu";
import { api } from "@/lib/api";
import { MessageSquare, ChevronDown, FlaskConical } from "lucide-react";
import { CoinIcon } from "@/components/CoinIcon";
import BrandGlyph from "@/components/BrandGlyph";

/* Center menu with a single highlight pill that glides between the hovered / active link. */
function AnimatedNav({ items }) {
  const location = useLocation();
  const [hovered, setHovered] = React.useState(null);
  const activeIdx = items.findIndex(
    (n) => location.pathname === n.to || location.pathname.startsWith(`${n.to}/`)
  );
  const highlight = hovered !== null ? hovered : activeIdx;

  return (
    <nav className="hidden lg:flex items-center gap-1" data-testid="site-nav" onMouseLeave={() => setHovered(null)}>
      {items.map((n, i) => {
        const on = i === highlight;
        const amber = n.accent === "amber";
        return (
          <NavLink key={n.testid} to={n.to} data-testid={n.testid} onMouseEnter={() => setHovered(i)}
            className="relative px-4 py-2 rounded-full text-[15px] font-medium whitespace-nowrap outline-none">
            {on && (
              <motion.span layoutId="nav-highlight"
                className={`absolute inset-0 rounded-full ${amber ? "bg-amber-400/15 ring-1 ring-amber-300/30" : "bg-white/10 ring-1 ring-violet-400/30"}`}
                transition={{ type: "spring", stiffness: 480, damping: 34 }} />
            )}
            <span className={`relative z-10 flex items-center gap-1.5 transition-colors duration-200 ${
              amber ? (on ? "text-amber-200" : "text-amber-200/90") : (on ? "text-white font-semibold" : "text-slate-200 hover:text-white")
            }`}>
              {n.logo && <img src={n.logo} alt="" className="w-6 h-6 object-contain" />}
              {n.label}
            </span>
            {n.badge > 0 && (
              <span data-testid="nav-support-badge" className="absolute -top-0.5 -right-0.5 z-20 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[11px] font-bold flex items-center justify-center">{n.badge}</span>
            )}
          </NavLink>
        );
      })}
    </nav>
  );
}

export default function Header() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [supportOpen, setSupportOpen] = React.useState(0);

  React.useEffect(() => {
    if (user?.role === "owner" || user?.role === "admin") {
      const fetchCount = () => api.get("/admin/escalations/count").then((r) => setSupportOpen(r.data.open || 0)).catch(() => {});
      fetchCount();
      const t = setInterval(fetchCount, 30000);
      return () => clearInterval(t);
    }
  }, [user]);

  const isStaff = user && (user.role === "owner" || user.role === "admin" || user.role === "brand_ambassador");
  const isMember = user && !isStaff;
  const adminLabel = user?.role === "owner" ? "Control Center" : user?.role === "brand_ambassador" ? "My Dashboard" : "Admin";

  const centerItems = () => {
    if (!user) {
      return [
        { to: "/talent", label: "Talent", testid: "nav-talent" },
        { to: "/jobs", label: "Jobs", testid: "nav-jobs" },
        { to: "/sales-types", label: "C.O.R.E.", testid: "nav-core", logo: "/core-logo.png" },
        { to: "/closing-process", label: "Train", testid: "nav-training" },
        { to: "/opportunities", label: "Opportunities", testid: "nav-opportunities" },
        { to: "/hire-sales-reps", label: "For Employers", testid: "nav-for-employers" },
        { to: "/about", label: "About", testid: "nav-about" },
      ];
    }
    if (user.role === "business") {
      return [
        { to: "/talent", label: "Browse Talent", testid: "nav-browse-talent" },
        { to: "/applicants", label: "My Jobs", testid: "nav-applicants" },
        { to: "/sales-types", label: "C.O.R.E.", testid: "nav-core", logo: "/core-logo.png" },
        { to: "/forms", label: "Forms", testid: "nav-forms-header" },
        { to: "/connie-connection", label: "The Connie Connection", testid: "nav-connie-connection", logo: "/connie-mark.png" },
      ];
    }
    if (user.role === "salesman") {
      return [
        { to: "/jobs", label: "Browse Jobs", testid: "nav-opportunities" },
        { to: "/my-applications", label: "My Applications", testid: "nav-my-applications" },
        { to: "/closing-process", label: "Train", testid: "nav-training" },
        { to: "/sales-types", label: "C.O.R.E.", testid: "nav-core", logo: "/core-logo.png" },
        { to: "/connie-connection", label: "The Connie Connection", testid: "nav-connie-connection", logo: "/connie-mark.png" },
      ];
    }
    const staff = [
      { to: "/sales-types", label: "C.O.R.E.", testid: "nav-core", logo: "/core-logo.png" },
      { to: "/connie-connection", label: "The Connie Connection", testid: "nav-connie-connection", logo: "/connie-mark.png" },
    ];
    staff.push({ to: "/admin", label: adminLabel, testid: "nav-admin", accent: "amber", logo: "/brain-mark.png", badge: (user.role === "owner" || user.role === "admin") ? supportOpen : 0 });
    return staff;
  };

  const items = centerItems();
  const iconBtn = "text-slate-200 hover:text-white hover:bg-white/10 rounded-full";

  const profileTrigger = (
    <button data-testid="header-profile-menu" aria-label="Profile" className="hidden lg:flex items-center gap-1.5 rounded-full pl-1 pr-2 py-1 bg-white/5 border border-white/10 hover:border-violet-400/50 hover:bg-white/10 transition-colors outline-none">
      <span className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center">
        <img src="/profile-icon.png" alt="" className="w-7 h-7 object-contain" />
      </span>
      <ChevronDown className="w-4 h-4 text-slate-300 opacity-70" />
    </button>
  );

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-slate-900/80 border-b border-white/10 shadow-[0_4px_30px_rgba(0,0,0,0.15)]" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between gap-4">
        <Logo compact className="shrink-0" />

        {/* Center, animated gliding menu (lg+) */}
        <AnimatedNav items={items} />

        {/* Right, grouped actions */}
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          {user ? (
            <>
              {user.role === "business" && (
                <div className="flex items-center gap-2.5">
                  <Link to="/pricing" data-testid="header-credits" title="Your Core Credits, tap to top up"
                    className="flex items-center gap-1.5 pl-2.5 pr-3.5 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-sm font-semibold hover:bg-emerald-500/20 hover:border-emerald-500/50 transition-colors">
                    <CoinIcon className="w-5 h-5" /> {user.credits ?? 0} <span className="text-emerald-400/70 font-normal">CC</span>
                  </Link>
                </div>
              )}

              {(isMember || user.role === "business" || user.role === "owner" || user.role === "admin") && (
                <div className="hidden sm:block h-6 w-px bg-white/10" />
              )}

              {(isMember || user.role === "business") && (
                <div className="flex items-center gap-1">
                  <button data-testid="header-messages" onClick={() => navigate("/messages")} title="Inbox"
                    className="relative p-2 rounded-lg hover:bg-white/10 transition-colors"><BrandGlyph name="inbox" size={22} active /></button>
                  <NotificationBell />
                </div>
              )}

              {(user.role === "owner" || user.role === "admin") && (
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" data-testid="header-messages-staff" onClick={() => navigate("/messages")} className={iconBtn} title="Messages"><MessageSquare className="w-4 h-4" /></Button>
                  <NotificationBell />
                </div>
              )}

              {user.real_role === "owner" && (
                <Button variant="ghost" size="sm" data-testid="header-test-mode"
                  onClick={() => window.dispatchEvent(new Event("open-test-mode"))}
                  className={`lg:hidden ${iconBtn} text-amber-300 hover:text-amber-200 hover:bg-amber-400/10`} title="Test Mode">
                  <FlaskConical className="w-4 h-4" />
                </Button>
              )}

              <ProfileMenu side="bottom" align="end" trigger={profileTrigger} />
            </>
          ) : (
            <>
              <Button variant="ghost" data-testid="header-signin" onClick={() => navigate("/login")} className="text-[15px] text-white hover:text-white hover:bg-white/10">Sign In</Button>
              <Button data-testid="header-post-skills" onClick={() => navigate("/signup")} className="rounded-full h-11 px-6 text-[15px] bg-violet-600 hover:bg-fuchsia-600 text-white font-semibold transition-colors duration-300">Get Started</Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
